import { Fragment, useEffect, useState } from 'react';
import {
  Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
  Cake, Umbrella, Wind, CalendarDays, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { api, CAS_PASMO } from '../api';

/**
 * Hodiny v čase predajne.
 *
 * Čas berieme z pásma Europe/Bratislava, nie z toho, čo má nastavené počítač –
 * keby mal niektorý v predajni pásmo pomýlené, ukazoval by iný čas než ostatné.
 * Sekundy idú vlastným krokom, nie odpočítavaním: keď sa notebook uspí,
 * po prebudení je čas hneď správny.
 */
function Hodiny() {
  const [teraz, setTeraz] = useState(() => new Date());

  useEffect(() => {
    let id;
    const tik = () => {
      setTeraz(new Date());
      // zarovnáme sa na celú sekundu, aby čísla neposkakovali
      id = setTimeout(tik, 1000 - (Date.now() % 1000) + 5);
    };
    tik();
    return () => clearTimeout(id);
  }, []);

  const cas = teraz.toLocaleTimeString('sk-SK', {
    timeZone: CAS_PASMO, hour: '2-digit', minute: '2-digit',
  });
  const sekundy = teraz.toLocaleTimeString('sk-SK', {
    timeZone: CAS_PASMO, second: '2-digit',
  }).padStart(2, '0');

  return (
    <div className="flex items-baseline gap-1.5 tabular-nums">
      <span className="text-5xl font-semibold leading-none tracking-tight text-slate-800">{cas}</span>
      <span className="text-xl font-medium leading-none text-slate-400">{sekundy}</span>
    </div>
  );
}

/* Server posiela názov obrázka, nie ikonu – rozhranie si ju priradí samo. */
const IKONY = {
  slnko: Sun,
  polooblacno: CloudSun,
  oblacno: Cloud,
  hmla: CloudFog,
  mrholenie: CloudDrizzle,
  dazd: CloudRain,
  sneh: CloudSnow,
  burka: CloudLightning,
};
const FARBY = {
  slnko: 'text-amber-500',
  polooblacno: 'text-amber-400',
  oblacno: 'text-slate-400',
  hmla: 'text-slate-400',
  mrholenie: 'text-sky-500',
  dazd: 'text-sky-600',
  sneh: 'text-sky-300',
  burka: 'text-indigo-500',
};

function Ikona({ druh, size = 20 }) {
  const I = IKONY[druh] || Cloud;
  return <I size={size} className={FARBY[druh] || 'text-slate-400'} />;
}

/**
 * Kalendárny týždeň podľa ISO 8601.
 *
 * Nie „koľkýkrát v roku nastal pondelok", ale norma, ktorou sa riadia
 * dodávatelia aj výrobcovia: týždeň patrí tomu roku, do ktorého padne jeho
 * **štvrtok**. Preto sa dátum najprv posunie na štvrtok toho istého týždňa
 * a až potom sa počíta. Bez toho by 1. januára vychádzal raz 1. a raz 53.
 *
 * Počíta sa v UTC, aby letný čas nespôsobil posun o deň.
 */
export function isoTyzden(datum) {
  const d = new Date(`${datum}T12:00:00`);
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const den = t.getUTCDay() || 7;                 // nedeľa (0) je siedmy deň
  t.setUTCDate(t.getUTCDate() + 4 - den);         // štvrtok toho istého týždňa
  const zaciatok = Date.UTC(t.getUTCFullYear(), 0, 1);
  return Math.ceil(((t - zaciatok) / 86400000 + 1) / 7);
}

/**
 * Mriežka mesiaca s listovaním.
 *
 * ── Prečo takto ──────────────────────────────────────────────────────────
 * Kalendár na Prehľade nie je ozdoba: plánujú sa podľa neho dodávky. Preto
 * **listuje dopredu aj dozadu** (termín „38. týždeň" býva o mesiac) a vľavo má
 * **číslo kalendárneho týždňa** – dodávatelia píšu termíny v týždňoch
 * a prepočítavať to z dátumu je zbytočná práca.
 *
 * Riadok aktuálneho týždňa je podfarbený, dnešok má plný krúžok. Sviatky sú
 * červené a pod mriežkou sú vypísané menom – „červený deň" bez mena núti
 * človeka najsť si, čo to je.
 *
 * Meniny sa ukazujú po kliknutí na deň, nie natlačené v bunke: v mriežke
 * širokej 18 rem sa meno nezmestí a skratka z neho nič nepovie.
 */
function Mriezka({ mesiac, dnes, na, vybrany, naDen }) {
  const prvy = new Date(`${mesiac.dni[0].datum}T12:00:00`).getDay();
  const prazdnych = (prvy + 6) % 7;              // nedeľa (0) je u nás siedmy deň

  const bunky = [...Array(prazdnych).fill(null), ...mesiac.dni];
  const tyzdne = [];
  for (let i = 0; i < bunky.length; i += 7) tyzdne.push(bunky.slice(i, i + 7));
  const tyzdenDnes = isoTyzden(dnes);
  const tentoMesiac = mesiac.dni.some((d) => d.datum === dnes);
  const sviatkyMesiaca = mesiac.dni.filter((d) => d.sviatok);

  return (
    <div className="w-[18.5rem] shrink-0">
      <div className="mb-2 flex items-center gap-1">
        <button type="button" aria-label="Predchádzajúci mesiac"
          className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          onClick={() => na(-1)}>
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1 text-center">
          <div className="text-sm font-semibold capitalize text-slate-700">
            {mesiac.nazov} {mesiac.rok}
          </div>
          {/* Cesta späť na dnešok. Bez nej sa človek po dvoch kliknutiach
              doprednú stratí a musí rátať, koľkokrát klikol. */}
          {!tentoMesiac && (
            <button type="button" className="text-[11px] text-brand-700 underline"
              onClick={() => na(0)}>späť na dnešok</button>
          )}
        </div>
        <button type="button" aria-label="Nasledujúci mesiac"
          className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          onClick={() => na(1)}>
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-[1.9rem_repeat(7,1fr)] text-center text-[11px] text-slate-400">
        <div title="Kalendárny týždeň" className="font-medium">tž</div>
        {['po', 'ut', 'st', 'št', 'pi', 'so', 'ne'].map((d, i) => (
          <div key={d} className={i >= 5 ? 'text-slate-300' : ''}>{d}</div>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-[1.9rem_repeat(7,1fr)] items-center text-center text-[13px]">
        {tyzdne.map((tyzden) => {
          /* Číslo sa berie z ktoréhokoľvek skutočného dňa v riadku – riadok je
             jeden týždeň, takže je pre všetky jeho dni rovnaké. */
          const prvyDen = tyzden.find(Boolean);
          const cislo = isoTyzden(prvyDen.datum);
          const tentoTyzden = tentoMesiac && cislo === tyzdenDnes;
          const pruh = tentoTyzden ? 'bg-brand-50/70' : '';
          return (
            <Fragment key={prvyDen.datum}>
              <div className={`rounded-l-md py-1 text-[11px] tabular-nums ${pruh} ${
                tentoTyzden ? 'font-semibold text-brand-700' : 'text-slate-300'}`}
              title={`${cislo}. kalendárny týždeň`}>
                {cislo}
              </div>
              {tyzden.map((d, i) => {
                if (!d) return <div key={`x${i}`} className={`py-1 ${pruh}`} />;
                const jeDnes = d.datum === dnes;
                const jeVybrany = d.datum === vybrany;
                const vikend = i >= 5;
                return (
                  <div key={d.datum}
                    className={`flex justify-center py-1 ${pruh} ${i === 6 ? 'rounded-r-md' : ''}`}>
                    <button type="button" onClick={() => naDen(d)}
                      title={[d.meniny && `${d.meniny}`, d.sviatok].filter(Boolean).join(' · ')}
                      className={[
                        'relative flex h-7 w-7 items-center justify-center rounded-full transition',
                        jeDnes ? 'bg-brand-600 font-semibold text-white'
                          : jeVybrany ? 'bg-slate-800 font-semibold text-white'
                            : d.sviatok ? 'font-semibold text-red-600 hover:bg-red-50'
                              : vikend ? 'text-slate-400 hover:bg-slate-100'
                                : 'text-slate-700 hover:bg-slate-100',
                      ].join(' ')}>
                      {d.den}
                      {d.sviatok && !jeDnes && !jeVybrany && (
                        <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-red-500" />
                      )}
                    </button>
                  </div>
                );
              })}
              {Array.from({ length: 7 - tyzden.length }, (_, i) => (
                <div key={`d${i}`} className={`py-1 ${pruh}`} />
              ))}
            </Fragment>
          );
        })}
      </div>

      {/* Sviatky menom. Červený deň bez mena núti človeka hľadať, čo to je. */}
      {!!sviatkyMesiaca.length && (
        <div className="mt-2 space-y-0.5 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
          {sviatkyMesiaca.map((d) => (
            <div key={d.datum} className="flex gap-1.5">
              <span className="w-8 shrink-0 text-right tabular-nums text-red-600">{d.den}.</span>
              <span className="min-w-0 truncate">{d.sviatok}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Dlaždica na šírku: dátum a meniny, kalendár mesiaca a počasie.
 *
 * Počasie smie chýbať – keď sa nedá načítať, zvyšok dlaždice funguje ďalej.
 * Prehľad je prvá stránka dňa a nemá padať na tom, že cudzia služba mlčí.
 */
export default function DenKalendar() {
  const [d, setD] = useState(null);
  /* Listovanie mesiacov. `posun` je počet mesiacov od dnešného – držať tu
     dátum by znamenalo riešiť prelom roka na dvoch miestach. */
  const [posun, setPosun] = useState(0);
  const [inyMesiac, setInyMesiac] = useState(null);
  /* Kliknutý deň. Meniny sa v mriežke nezmestia, tak sa ukážu vedľa. */
  const [vybrany, setVybrany] = useState(null);

  useEffect(() => {
    let zive = true;
    const nacitaj = () => api.get('/api/stats/den').then((v) => { if (zive) setD(v); }).catch(() => {});
    nacitaj();
    // po polnoci má sedieť dátum aj vtedy, keď stránka ostala otvorená
    const t = setInterval(nacitaj, 5 * 60_000);
    return () => { zive = false; clearInterval(t); };
  }, []);

  /* Iný mesiac sa dopýta servera – meniny aj Veľká noc sa počítajú tam. */
  useEffect(() => {
    if (!posun || !d) { setInyMesiac(null); return undefined; }
    let zive = true;
    const zaklad = new Date(d.mesiac.rok, d.mesiac.cislo - 1 + posun, 1);
    api.get(`/api/stats/mesiac?rok=${zaklad.getFullYear()}&mesiac=${zaklad.getMonth() + 1}`)
      .then((v) => { if (zive) setInyMesiac(v); })
      .catch(() => {});
    return () => { zive = false; };
  }, [posun, d?.mesiac?.cislo, d?.mesiac?.rok]);

  if (!d) return null;
  const p = d.pocasie;
  const mesiac = (posun && inyMesiac) || d.mesiac;
  /* Vybraný deň platí len v mesiaci, v ktorom sa naň kliklo. */
  const denVybrany = mesiac.dni.find((x) => x.datum === vybrany) || null;

  const naMesiac = (kam) => {
    setPosun((x) => (kam === 0 ? 0 : x + kam));
    setVybrany(null);
  };

  return (
    <div className="card-p">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">

        {/* hodiny, dátum a meniny */}
        <div className="min-w-0 flex-1">
          <Hodiny />
          <div className="mt-1.5 text-sm capitalize text-slate-500">{d.dnes.den}</div>
          <div className="text-2xl font-semibold leading-tight text-slate-800">
            {d.dnes.cislo}. {d.dnes.mesiac2p} {d.dnes.rok}
          </div>
          {/* Dodávatelia píšu termíny v týždňoch – nech to netreba rátať. */}
          <div className="mt-0.5 text-sm text-slate-500">
            {isoTyzden(d.dnes.datum)}. kalendárny týždeň
          </div>

          {d.dnes.sviatok && (
            <div className="mt-2 inline-block rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700">
              {d.dnes.sviatok}
            </div>
          )}

          <div className="mt-4 flex items-start gap-2.5">
            <Cake size={18} className="mt-0.5 shrink-0 text-brand-600" />
            <div className="min-w-0">
              <div className="text-sm text-slate-500">Meniny má</div>
              <div className="text-lg font-semibold text-slate-800">{d.dnes.meniny || '—'}</div>
              {d.zajtra.meniny && (
                <div className="mt-0.5 text-xs text-slate-400">zajtra {d.zajtra.meniny}</div>
              )}
            </div>
          </div>

          {/* Kliknutý deň – meniny a sviatok. Nahrádza „najbližší sviatok",
              kým je niečo vybraté; dve podobné informácie pod sebou by len
              zaberali miesto. */}
          {denVybrany && denVybrany.datum !== d.dnes.datum && (
            <div className="mt-4 flex items-start gap-2.5">
              <CalendarDays size={18} className="mt-0.5 shrink-0 text-slate-800" />
              <div className="min-w-0">
                <div className="text-sm text-slate-500">
                  {denVybrany.den}. {mesiac.nazov} {mesiac.rok}
                  <span className="text-slate-400"> · {isoTyzden(denVybrany.datum)}. týždeň</span>
                </div>
                <div className="text-sm font-medium text-slate-700">
                  {denVybrany.meniny ? `Meniny má ${denVybrany.meniny}` : 'Meniny nikto'}
                </div>
                {denVybrany.sviatok && (
                  <div className="text-xs font-medium text-red-600">{denVybrany.sviatok}</div>
                )}
              </div>
            </div>
          )}

          {d.najblizsiSviatok && !d.dnes.sviatok && !denVybrany && (
            <div className="mt-4 flex items-start gap-2.5">
              <CalendarDays size={18} className="mt-0.5 shrink-0 text-slate-300" />
              <div className="min-w-0">
                <div className="text-sm text-slate-500">Najbližší sviatok</div>
                <div className="text-sm font-medium text-slate-700">{d.najblizsiSviatok.nazov}</div>
                <div className="text-xs text-slate-400">
                  {d.najblizsiSviatok.den}. {d.najblizsiSviatok.mesiac2p}
                  {d.najblizsiSviatok.oDni === 1 ? ' · zajtra' : ` · o ${d.najblizsiSviatok.oDni} dní`}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* kalendár */}
        <div className="flex justify-center lg:border-x lg:border-slate-100 lg:px-8">
          <Mriezka mesiac={mesiac} dnes={d.dnes.datum} na={naMesiac}
            vybrany={vybrany} naDen={(x) => setVybrany((z) => (z === x.datum ? null : x.datum))} />
        </div>

        {/* počasie */}
        <div className="min-w-0 flex-1">
          {!p ? (
            <div className="text-sm text-slate-400">
              Počasie sa nepodarilo načítať{d.chybaPocasia ? ` – ${d.chybaPocasia}` : ''}.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Ikona druh={p.teraz.ikona} size={40} />
                <div>
                  <div className="text-3xl font-semibold leading-tight text-slate-800">
                    {p.teraz.teplota} °C
                  </div>
                  <div className="text-sm text-slate-500">{p.teraz.popis} · {p.miesto}</div>
                </div>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-slate-400">
                {p.dni[0] && <span>dnes {p.dni[0].max}° / {p.dni[0].min}°</span>}
                <span>pocitovo {p.teraz.pocit} °C</span>
                <span className="flex items-center gap-1"><Wind size={12} />{p.teraz.vietor} km/h</span>
              </div>

              <div className="mt-4 flex gap-2">
                {p.dni.slice(1).map((x) => (
                  <div key={x.datum} className="flex-1 rounded-lg bg-slate-50 px-2 py-2 text-center">
                    <div className="text-[11px] capitalize text-slate-500">{x.den.slice(0, 2)}</div>
                    <div className="my-1 flex justify-center" title={x.popis}>
                      <Ikona druh={x.ikona} size={18} />
                    </div>
                    <div className="text-[13px] font-semibold text-slate-700">{x.max}°</div>
                    <div className="text-[11px] text-slate-400">{x.min}°</div>
                    {x.dazd > 20 && (
                      <div className="mt-0.5 flex items-center justify-center gap-0.5 text-[10px] text-sky-600">
                        <Umbrella size={9} />{x.dazd} %
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
